import * as d3 from 'd3-force';
import { ForceGraphConfig, ForceGraphData, ForceNode, ForceLink } from './config/forceGraphTypes';

export class ForceSimulation {
    public simulation: d3.Simulation<ForceNode, ForceLink>;
    private nodes: ForceNode[] = [];
    private links: ForceLink[] = [];
    private onTick: () => void;

    constructor(onTick: () => void) {
        this.onTick = onTick;
        this.simulation = d3.forceSimulation<ForceNode, ForceLink>()
            .on('tick', this.onTick);
    }

    public setOnTick(onTick: () => void) {
        this.onTick = onTick;
        this.simulation.on('tick', this.onTick);
    }

    public updateData(data: ForceGraphData) {
        this.nodes = data.nodes;
        this.links = data.links;

        this.simulation.nodes(this.nodes);

        // Re-initialize link force
        const linkForce = d3.forceLink<ForceNode, ForceLink>(this.links)
            .id(d => d.id);

        this.simulation.force('link', linkForce);
        this.simulation.alpha(1).restart();
    }

    public updateConfig(config: ForceGraphConfig, width: number, height: number) {
        const {
            repulsionStrength,
            linkDistance,
            linkStrength,
            collisionPadding,
            gravityStrength,
            layoutMode
        } = config;

        // Center Force
        this.simulation.force('center', d3.forceCenter(width / 2, height / 2));

        // Charge (Repulsion)
        this.simulation.force('charge', d3.forceManyBody().strength(-repulsionStrength));

        // Link Force
        const linkForce = this.simulation.force<d3.ForceLink<ForceNode, ForceLink>>('link');
        if (linkForce) {
            linkForce
                .distance(linkDistance)
                .strength(linkStrength);
        }

        // Collision
        this.simulation.force('collide', d3.forceCollide().radius((d: any) => (d.radius || 5) + collisionPadding));

        // Radial / Hierarchy Layouts
        if (layoutMode === 'radial') {
            this.simulation.force('charge', d3.forceManyBody().strength(-repulsionStrength * 0.5));
            this.simulation.force('radial', d3.forceRadial(Math.min(width, height) / 3, width / 2, height / 2).strength(0.8));
        } else if (layoutMode === 'hierarchy') {
            // Simple hierarchical simulation using radial (concentric) or Y-positioning
            // For now, let's use a tighter radial layout to distinguish it, or a tree-like force
            // Using a distinct radial force for hierarchy representation
            this.simulation.force('charge', d3.forceManyBody().strength(-repulsionStrength * 1.5)); // Stronger repulsion
            this.simulation.force('radial', d3.forceRadial(Math.min(width, height) / 2.5, width / 2, height / 2).strength(1));
        } else {
            this.simulation.force('radial', null);
        }

        this.simulation.alphaTarget(0.01).restart();
    }

    public stop() {
        this.simulation.stop();
    }

    public getNodes() {
        return this.nodes;
    }

    public getLinks() {
        return this.links;
    }

    // Interaction Helpers
    public dragStart(node: ForceNode) {
        if (!d3.event?.active) this.simulation.alphaTarget(0.3).restart();
        node.fx = node.x;
        node.fy = node.y;
    }

    public drag(node: ForceNode, x: number, y: number) {
        node.fx = x;
        node.fy = y;
    }

    public dragEnd(node: ForceNode, pinOnDrag: boolean) {
        if (!d3.event?.active) this.simulation.alphaTarget(0);
        if (!pinOnDrag) {
            node.fx = null;
            node.fy = null;
        }
    }
}
